import React from 'react';

import {
  faCheck,
  faQuestion,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { palette, lightPalette } from '@app/theme';
import { ScrollableTable } from '@components/ui/ScrollableTable';

import type { DealBreakerTableProperties } from './DealBreakerTable.types';
import type { DealBreakerStatus } from '@api/aiReviews/api.aiReviews.reviewTypes';



type StatusConfig = {
  label: string;
  color: string;
  icon: typeof faCheck;
};

const STATUS_CONFIG: Record<DealBreakerStatus, StatusConfig> = {
  ok: {
    label: 'Пройден',
    color: palette.success.main,
    icon: faCheck,
  },
  critical: {
    label: 'Критично',
    color: palette.error.main,
    icon: faXmark,
  },
  warning: {
    label: 'Требует внимания',
    color: palette.warning.main,
    icon: faTriangleExclamation,
  },
  unknown: {
    label: 'Не определён',
    color: '#9E9E9E',
    icon: faQuestion,
  },
};

export const DealBreakerTable: React.FC<DealBreakerTableProperties> = ({
  dealBreakers,
  dataTest = 'deal-breaker-table',
}) => {
  return (
    <ScrollableTable
      dataTest={dataTest}
      ariaLabel='Стоп-факторы'
      // Именно светлый токен, а не тема-зависимый `background.paper`: вся таблица (и подсказка
      // о скролле) свёрстана на `lightPalette`, тема-зависимый фон рассогласовался бы с ними
      sx={{ backgroundColor: lightPalette.background.paper }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: lightPalette.border.light }}>
            <TableCell
              data-testid={`${dataTest}_header-criterion`}
              sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}
            >
              Критерий
            </TableCell>
            <TableCell
              data-testid={`${dataTest}_header-status`}
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
                py: 1.5,
                width: 180,
              }}
            >
              Статус
            </TableCell>
            <TableCell
              data-testid={`${dataTest}_header-details`}
              sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}
            >
              Детали
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {dealBreakers.map((item, index) => {
            const config = STATUS_CONFIG[item.status];
            const isEven = index % 2 === 0;

            return (
              <TableRow
                key={`${item.criterion}-${index}`}
                data-testid={`${dataTest}_row-${index}`}
                sx={{
                  backgroundColor: isEven ? 'background.paper' : lightPalette.background.default,
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                <TableCell
                  data-testid={`${dataTest}_row-${index}_criterion`}
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    fontWeight: 500,
                    py: 1.5,
                  }}
                >
                  {item.criterion}
                </TableCell>

                <TableCell
                  data-testid={`${dataTest}_row-${index}_status`}
                  sx={{ py: 1.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FontAwesomeIcon
                      icon={config.icon}
                      style={{ color: config.color, fontSize: 14, flexShrink: 0 }}
                    />
                    <Typography
                      sx={{ fontSize: '0.8125rem', fontWeight: 600, color: config.color }}
                    >
                      {config.label}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell
                  data-testid={`${dataTest}_row-${index}_details`}
                  sx={{
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    lineHeight: 1.55,
                    py: 1.5,
                  }}
                >
                  {item.details}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
};
